import { ClerkProvider } from '@clerk/react-router';
import { useEffect, useState, type ComponentProps } from 'react';
import { useLocation } from 'react-router';
import App from './app';
import { ClerkPublicAuthBridge } from './components/headers/PublicHeader/publicAuth';
import { isOnHlxHost } from './config/branding';
import { AuthLoading } from './pages/authentication/AuthLoading';
import { PublicApp } from './PublicApp';
import type { BundledClerkOptions } from './routing/clerkBundle';
import { PendingSessionBoundary } from './routing/PendingSessionBoundary';
import { isPublicSitePath } from './routing/publicSiteRoutes';
import { readSignedInHint } from './routing/signedInHint';

export type ClerkProviderConfig = Omit<ComponentProps<typeof ClerkProvider>, 'children'>;

export const ApplicationBoundary = ({
  clerkConfig,
  bundledClerk,
}: {
  clerkConfig: ClerkProviderConfig;
  /** [MCV-1789] clerk-js and Clerk UI downloading first-party; absent when the CDN loads them. */
  bundledClerk?: Promise<BundledClerkOptions>;
}) => {
  const { pathname } = useLocation();
  const isPublicPath = isPublicSitePath(pathname);
  // [MCV-1723] Read once per mount. A browser last seen signed in boots Clerk on
  // a cold public page, so the public header can show who is signed in instead
  // of "Log in" (a signed-in user reaches these pages from the app footer's legal
  // links, which open in a new tab, or by visiting the site directly). This is
  // only a hint, never authentication: Clerk stays the source of truth and
  // ClerkPublicAuthBridge corrects a stale one.
  const [hasSignedInHint] = useState(readSignedInHint);
  const routeRequiresClerk =
    !isPublicPath || isOnHlxHost(window.location.hostname) || hasSignedInHint;

  // [MCV-1589] A cold primary-site marketing page must remain independent of
  // Clerk for an anonymous visitor, but once Clerk has booted it must survive
  // client-side navigation through `/`. Signing out lands on `/`, and
  // unmounting the provider there left the next sign-up mount in a blank/grey
  // recovery state.
  const [hasMountedClerk, setHasMountedClerk] = useState(routeRequiresClerk);

  useEffect(() => {
    if (routeRequiresClerk && !hasMountedClerk) {
      setHasMountedClerk(true);
    }
  }, [hasMountedClerk, routeRequiresClerk]);

  // [MCV-1789] The bundle download starts at boot (main.tsx). Only trees that mount
  // Clerk wait for it; Clerk-free public pages never do. ClerkProvider mounts once,
  // with its final props: IsomorphicClerk is a singleton keyed on `options.Clerk`, so
  // mounting on the CDN config and switching later would not take effect. With no
  // promise (production) there is nothing to wait for and the first render mounts it.
  const [bundled, setBundled] = useState<BundledClerkOptions | null>(bundledClerk ? null : {});

  useEffect(() => {
    if (!bundledClerk) return;
    let active = true;
    // loadBundledClerkOptions never rejects, but the prop accepts any promise: a
    // rejection still has to release the gate, or Clerk routes would spin forever.
    void bundledClerk.then(
      (options) => {
        if (active) setBundled(options);
      },
      () => {
        if (active) setBundled({});
      }
    );
    return () => {
      active = false;
    };
  }, [bundledClerk]);

  const content = isPublicPath ? <PublicApp /> : <App />;

  if (isPublicPath && !routeRequiresClerk && !hasMountedClerk) {
    // Public marketing/legal pages render Clerk-free for an ANONYMOUS cold visitor
    // on the PRIMARY (medcredvault) for resilience — they must stay up even if
    // auth is down. A browser carrying the signed-in hint is the exception (see
    // `hasSignedInHint`), and it gets the Clerk branch below.
    //
    // On the HLX SATELLITE they must still boot clerk-js. The satellite session
    // rides across the primary<->satellite hops on the `__clerk_db_jwt` dev-browser
    // token, which only a mounted clerk-js consumes/persists. A Clerk-less satellite
    // page drops that token; browsers that carry the dev-browser as a third-party
    // cookie (Chrome) silently recover it, but ones that block third-party cookies
    // (Safari ITP) cannot, so the satellite session never settles and login loops.
    // Before #979 ClerkProvider wrapped every route, so this always held; #979 made
    // these paths Clerk-less for BOTH hosts and regressed satellite login in Safari.
    return content;
  }

  // AuthLoading is what ClerkLoading would show while clerk-js boots anyway.
  if (!bundled) return <AuthLoading />;

  // The provider tree is identical on every render so <App/> is never remounted:
  // ClerkPublicAuthBridge is always present and only feeds the public header.
  return (
    <ClerkProvider {...clerkConfig} {...bundled}>
      <PendingSessionBoundary>
        <ClerkPublicAuthBridge>{content}</ClerkPublicAuthBridge>
      </PendingSessionBoundary>
    </ClerkProvider>
  );
};
